import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProjectSummary from './components/ProjectSummary';
import DownloadProgress from './components/DownloadProgress';
import NextStepsGuide from './components/NextStepsGuide';
import ShareOptions from './components/ShareOptions';
import ErrorDisplay from './components/ErrorDisplay';

const DownloadModal = () => {
  const navigate = useNavigate();
  const [downloadState, setDownloadState] = useState('preparing');
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('preparing');
  const [error, setError] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);

  const projectData = {
    name: "E-Commerce Platform",
    fileCount: 87,
    compressedSize: "4.2 MB",
    components: [
      { name: "React Frontend", icon: "Layout", color: "text-blue-400" },
      { name: "Node.js Backend", icon: "Server", color: "text-green-400" },
      { name: "PostgreSQL Schemas", icon: "Database", color: "text-purple-400" },
      { name: "API Documentation", icon: "FileText", color: "text-yellow-400" },
      { name: "Auth Boilerplate", icon: "Lock", color: "text-red-400" },
      { name: "Docker Config", icon: "Box", color: "text-cyan-400" }
    ]
  };

  useEffect(() => {
    if (downloadState === 'preparing') {
      simulateDownloadProcess();
    }
  }, [downloadState]);

  const simulateDownloadProcess = () => {
    const stages = [
      { name: 'preparing', duration: 1500, progress: 25 },
      { name: 'compressing', duration: 2000, progress: 50 },
      { name: 'uploading', duration: 2500, progress: 75 },
      { name: 'finalizing', duration: 1000, progress: 100 }
    ];

    let currentProgress = 0;
    let stageIndex = 0;

    const processStage = () => {
      if (stageIndex >= stages?.length) {
        setDownloadState('complete');
        return;
      }

      const stage = stages?.[stageIndex];
      setCurrentStage(stage?.name);

      const increment = (stage?.progress - currentProgress) / 20;
      const interval = stage?.duration / 20;

      const progressInterval = setInterval(() => {
        currentProgress += increment;
        setProgress(Math.min(currentProgress, stage?.progress));

        if (currentProgress >= stage?.progress) {
          clearInterval(progressInterval);
          stageIndex++;
          setTimeout(processStage, 200);
        }
      }, interval);
    };

    processStage();
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '#';
    link.download = `${projectData?.name?.replace(/\s+/g, '_')}.zip`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    setTimeout(() => {
      navigate('/project-dashboard');
    }, 1500);
  };

  const handleRetry = () => {
    setError(null);
    setDownloadState('preparing');
    setProgress(0);
    setCurrentStage('preparing');
    simulateDownloadProcess();
  };

  const handleCreateVariation = () => {
    navigate('/prompt-input', { state: { variation: true, baseProject: projectData?.name } });
  };

  const handleViewDashboard = () => {
    navigate('/project-dashboard');
  };

  const handleClose = () => {
    navigate('/file-explorer');
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation />
      <div className="content-wrapper">
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 flex items-center justify-center p-4 md:p-6 lg:p-8">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary flex items-center justify-center">
                  <Icon name="Download" size={24} color="#FFFFFF" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl lg:text-2xl font-semibold">Download Project</h2>
                  <p className="text-xs md:text-sm text-muted-foreground">{projectData?.name}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-muted rounded-md transition-colors"
                aria-label="Close modal"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6">
              {error ? (
                <ErrorDisplay
                  error={error}
                  onRetry={handleRetry}
                  onViewDashboard={handleViewDashboard}
                />
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                    <ProjectSummary projectData={projectData} />
                    <DownloadProgress
                      progress={progress}
                      stage={currentStage}
                      isComplete={downloadState === 'complete'}
                    />
                  </div>

                  {downloadState === 'complete' && (
                    <div className="space-y-4 md:space-y-6">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          variant="default"
                          fullWidth
                          iconName="Download"
                          iconPosition="left"
                          onClick={handleDownload}
                          className="text-base md:text-lg py-3 md:py-4"
                        >
                          Download ZIP File
                        </Button>
                        <Button
                          variant="outline"
                          fullWidth
                          iconName="Copy"
                          iconPosition="left"
                          onClick={handleCreateVariation}
                        >
                          Create Variation
                        </Button>
                      </div>

                      <div className="flex items-center justify-center gap-4">
                        <button
                          onClick={() => setShowShareOptions(!showShareOptions)}
                          className="flex items-center gap-2 px-4 py-2 text-sm md:text-base text-primary hover:bg-primary/10 rounded-md transition-colors"
                        >
                          <Icon name="Share2" size={18} />
                          <span>{showShareOptions ? 'Hide' : 'Show'} Share Options</span>
                        </button>
                        <button
                          onClick={handleViewDashboard}
                          className="flex items-center gap-2 px-4 py-2 text-sm md:text-base text-muted-foreground hover:bg-muted rounded-md transition-colors"
                        >
                          <Icon name="Folder" size={18} />
                          <span>View All Projects</span>
                        </button>
                      </div>

                      {showShareOptions && (
                        <div className="animate-fade-in">
                          <ShareOptions projectId="proj_ecommerce_2026" />
                        </div>
                      )}

                      <NextStepsGuide />

                      <div className="bg-muted rounded-lg p-4 md:p-6">
                        <div className="flex items-start gap-3">
                          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm md:text-base font-semibold mb-2">Important Notes:</h4>
                            <ul className="space-y-1 text-xs md:text-sm text-muted-foreground">
                              <li className="flex items-start gap-2">
                                <span className="flex-shrink-0 mt-1">•</span>
                                <span>This project is saved to your dashboard and can be re-downloaded anytime</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="flex-shrink-0 mt-1">•</span>
                                <span>Make sure to update environment variables before deployment</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="flex-shrink-0 mt-1">•</span>
                                <span>Review the README.md file for detailed setup instructions</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="flex-shrink-0 mt-1">•</span>
                                <span>Database migrations must be run before starting the application</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;